import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../utils/permissions';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';
import { projectService, Project, ProjectTask, ProjectUser } from '../../services/projectService';
import ProjectTaskModal from './ProjectTaskModal';
import ProjectTaskList from './ProjectTaskList';
import ProjectDetails from './ProjectDetails';
import ProjectsTrialBanner from './ProjectsTrialBanner';
import { canOpenProjectTask } from './projectTaskAccess';
import { getStoredProjectViewMode, setStoredProjectViewMode } from '../../utils/projectViewPreferences';
import StageActions, { StageStarIcon } from './ProjectStageActions';
import { AssigneeAvatar, assigneesOf, dependencyLabel, formatTaskDueDate } from './projectTaskDisplay';
import './Projects.css';

const SortableTask: React.FC<{
  task: ProjectTask;
  onOpen: () => void;
  canOpen: boolean;
}> = ({ task, onOpen, canOpen }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: task._id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  const people = assigneesOf(task);
  const pct = task.completionPercent || 0;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`task-card${task.hasUnreadActivity ? ' has-activity' : ''}${canOpen ? '' : ' is-locked'}`}
      {...attributes}
      {...listeners}
      onClick={canOpen ? onOpen : undefined}
      title={canOpen ? undefined : 'You are not assigned to this task'}
    >
      <h4>
        {task.hasUnreadActivity && <span className="activity-dot" title="New activity" />}
        {task.title}
      </h4>
      <div className="task-card-fields">
        <div className="task-card-field">
          <em>Depends on</em>
          <span>{dependencyLabel(task)}</span>
        </div>
        <div className="task-card-field">
          <em>Assignee</em>
          {people.length ? (
            <span className="list-avatars">
              {people.map((person) => (
                <AssigneeAvatar key={person._id} user={person} />
              ))}
            </span>
          ) : (
            <span>—</span>
          )}
        </div>
        <div className="task-card-field">
          <em>Due date</em>
          <span>{formatTaskDueDate(task.dueDate)}</span>
        </div>
      </div>
      <div className="task-card-meta">
        <span className={`priority-pill priority-${task.priority}`}>{task.priority}</span>
        <span className="list-progress">
          <span className="list-progress-track">
            <span style={{ width: `${pct}%` }} />
          </span>
          <span>{pct}%</span>
        </span>
      </div>
    </div>
  );
};

const Column: React.FC<{
  id: string;
  title: string;
  color: string;
  tasks: ProjectTask[];
  index: number;
  total: number;
  finished: boolean;
  canManage: boolean;
  showSetFinished: boolean;
  onOpen: (task: ProjectTask) => void;
  canOpenTask: (task: ProjectTask) => boolean;
  onAdd: () => void;
  onReorder: (direction: 'up' | 'down') => void;
  onDelete: () => void;
  onSetFinished: () => void;
}> = ({
  id,
  title,
  color,
  tasks,
  index,
  total,
  finished,
  canManage,
  showSetFinished,
  onOpen,
  canOpenTask,
  onAdd,
  onReorder,
  onDelete,
  onSetFinished
}) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <div className="kanban-column" ref={setNodeRef} style={{ borderTopColor: color }}>
      <div className="kanban-column-header">
        <div className="kanban-column-title-row">
          <div className="kanban-column-title">{title}</div>
          {finished && (
            <span className="stage-star" title="Finished stage">
              <StageStarIcon filled />
            </span>
          )}
          <span className="kanban-count">{tasks.length}</span>
        </div>
        {canManage && (
          <StageActions
            index={index}
            total={total}
            axis="horizontal"
            showSetFinished={showSetFinished}
            onReorder={onReorder}
            onDelete={onDelete}
            onSetFinished={onSetFinished}
          />
        )}
      </div>
      <SortableContext items={tasks.map((task) => task._id)} strategy={verticalListSortingStrategy}>
        {tasks.map((task) => (
          <SortableTask
            key={task._id}
            task={task}
            canOpen={canOpenTask(task)}
            onOpen={() => onOpen(task)}
          />
        ))}
      </SortableContext>
      <button type="button" className="kanban-add-task" onClick={onAdd}>
        Add task
      </button>
    </div>
  );
};

const ProjectBoard: React.FC = () => {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const canManage = can(Permission.MANAGE_PROJECTS);
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [users, setUsers] = useState<ProjectUser[]>([]);
  const [error, setError] = useState('');
  const [view, setView] = useState<'kanban' | 'list' | 'details'>(() =>
    getStoredProjectViewMode(user?._id)
  );
  const [modalTask, setModalTask] = useState<ProjectTask | null | undefined>(undefined);
  const [newTaskStatus, setNewTaskStatus] = useState<string | undefined>();
  const [stageDialog, setStageDialog] = useState(false);
  const [columnTitle, setColumnTitle] = useState('');
  const [columnSetFinished, setColumnSetFinished] = useState(false);
  const creatorId = project?.createdBy?._id || (typeof project?.createdBy === 'string' ? project.createdBy : '');
  const isCreator = Boolean(creatorId && user?._id && creatorId === user._id);
  const canArchiveProject = isCreator || user?.role === UserRole.SUPER_ADMIN;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    setView((current) => (current === 'details' ? current : getStoredProjectViewMode(user?._id)));
  }, [user?._id]);

  useEffect(() => {
    if (view === 'details' && project && !isCreator) {
      setView(getStoredProjectViewMode(user?._id));
    }
  }, [view, project, isCreator, user?._id]);

  const changeView = (next: 'kanban' | 'list' | 'details') => {
    setView(next);
    if (next === 'kanban' || next === 'list') {
      setStoredProjectViewMode(user?._id, next);
    }
  };
  const focusTaskId = searchParams.get('task') || undefined;
  const focusCommentId = searchParams.get('comment') || undefined;

  const load = async () => {
    if (!projectId) return;
    try {
      const [nextProject, nextTasks, nextUsers] = await Promise.all([
        projectService.getProject(projectId),
        projectService.listTasks(projectId),
        projectService.listUsers(projectId)
      ]);
      setProject(nextProject);
      setTasks(nextTasks);
      setUsers(nextUsers);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load project');
    }
  };

  useEffect(() => {
    load();
  }, [projectId]);

  useEffect(() => {
    if (!focusTaskId || !tasks.length) return;
    const found = tasks.find((task) => task._id === focusTaskId);
    if (found && canOpenProjectTask(found, user, canManage)) setModalTask(found);
  }, [focusTaskId, tasks]);

  const columns = useMemo(
    () => (project?.columns || []).slice().sort((a, b) => a.order - b.order),
    [project]
  );

  const tasksByColumn = useMemo(() => {
    const map: Record<string, ProjectTask[]> = {};
    columns.forEach((column) => {
      map[column.id] = tasks.filter((task) => task.status === column.id);
    });
    return map;
  }, [columns, tasks]);

  const canOpenTask = (task: ProjectTask) => canOpenProjectTask(task, user, canManage);
  const unreadCount = tasks.filter((task) => task.hasUnreadActivity).length;

  const openExistingTask = (task: ProjectTask) => {
    if (!canOpenTask(task)) return;
    setModalTask(task);
    if (task.hasUnreadActivity) {
      projectService.markTaskSeen(task._id).catch(() => undefined);
      setTasks((prev) => prev.map((item) => (item._id === task._id ? { ...item, hasUnreadActivity: false } : item)));
    }
  };

  const markAllSeen = async () => {
    if (!project) return;
    await projectService.acknowledge(project._id);
    setTasks((prev) => prev.map((task) => ({ ...task, hasUnreadActivity: false })));
  };

  const openNewTask = (status?: string) => {
    setNewTaskStatus(status);
    setModalTask(null);
  };

  const addStage = async () => {
    if (!columnTitle.trim() || !project) return;
    try {
      await projectService.addColumn(project._id, {
        title: columnTitle.trim(),
        setAsFinished: !project.finishedColumnId && columnSetFinished
      });
      setColumnTitle('');
      setColumnSetFinished(false);
      setStageDialog(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not add stage');
    }
  };

  const reorderStage = async (columnId: string, direction: 'up' | 'down') => {
    if (!project) return;
    const ids = columns.map((column) => column.id);
    const index = ids.indexOf(columnId);
    const next = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || next < 0 || next >= ids.length) return;
    const swapped = [...ids];
    [swapped[index], swapped[next]] = [swapped[next], swapped[index]];
    try {
      const updated = await projectService.reorderColumns(project._id, swapped);
      setProject(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not reorder stages');
    }
  };

  const deleteStage = async (columnId: string) => {
    if (!project) return;
    if (columns.length <= 1) {
      setError('Cannot delete the last stage');
      return;
    }
    const column = columns.find((item) => item.id === columnId);
    if (!window.confirm(`Delete stage “${column?.title || ''}”? Move or delete tasks in it first.`)) {
      return;
    }
    try {
      await projectService.deleteColumn(project._id, columnId);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not delete stage');
    }
  };

  const setFinishedStage = async (columnId: string) => {
    if (!project) return;
    try {
      const updated = await projectService.setFinishedColumn(project._id, columnId);
      setProject(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not set finished stage');
    }
  };

  const closeModal = () => {
    setModalTask(undefined);
    setNewTaskStatus(undefined);
    if (searchParams.get('task')) {
      searchParams.delete('task');
      searchParams.delete('comment');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const onDragEnd = async (event: DragEndEvent) => {
    const taskId = String(event.active.id);
    const overId = event.over?.id ? String(event.over.id) : '';
    if (!overId) return;
    const overColumn = columns.find((column) => column.id === overId)?.id
      || tasks.find((task) => task._id === overId)?.status;
    if (!overColumn) return;
    const task = tasks.find((item) => item._id === taskId);
    if (!task || task.status === overColumn) return;
    setTasks((prev) => prev.map((item) => (item._id === taskId ? { ...item, status: overColumn } : item)));
    try {
      const updated = await projectService.updateTaskStatus(taskId, overColumn);
      setTasks((prev) => prev.map((item) => (item._id === taskId ? updated : item)));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not move task');
      load();
    }
  };

  if (!project) {
    return <div className="projects-module">{error || 'Loading…'}</div>;
  }

  return (
    <div className="projects-module">
      <div className="project-page-head">
        <div className="project-page-title-row">
          <button type="button" className="project-back" onClick={() => navigate('/projects')}>← Projects</button>
          <h1 className="project-page-title">{project.name}</h1>
          <span className={`project-status-pill${project.status === 'archived' ? ' is-archived' : ''}`}>
            {project.status === 'archived' ? 'Archived' : 'Active'}
          </span>
        </div>
        <div className="projects-view-toggle">
          <button type="button" className={view === 'kanban' ? 'is-active' : ''} onClick={() => changeView('kanban')}>
            Board
          </button>
          <button type="button" className={view === 'list' ? 'is-active' : ''} onClick={() => changeView('list')}>
            List
          </button>
          {isCreator && (
            <button type="button" className={view === 'details' ? 'is-active' : ''} onClick={() => changeView('details')}>
              Project details
            </button>
          )}
          {canArchiveProject && (
            <button
              type="button"
              onClick={async () => {
                try {
                  const archived = project.status !== 'archived';
                  const updated = await projectService.archiveProject(project._id, archived);
                  setProject(updated);
                } catch (err: any) {
                  setError(err.response?.data?.message || 'Could not archive project');
                }
              }}
            >
              {project.status === 'archived' ? 'Unarchive' : 'Archive'}
            </button>
          )}
          {canArchiveProject && project.status === 'archived' && (
            <button
              type="button"
              className="is-danger"
              onClick={async () => {
                if (!window.confirm(`Permanently delete “${project.name}”? This cannot be undone.`)) return;
                try {
                  await projectService.deleteProject(project._id);
                  navigate('/projects');
                } catch (err: any) {
                  setError(err.response?.data?.message || 'Could not delete project');
                }
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
      <ProjectsTrialBanner company={typeof project.company === 'object' ? project.company : undefined} />
      {error && <p className="usage-error">{error}</p>}
      {unreadCount > 0 && (
        <div className="activity-banner">
          <div>
            <strong>
              {unreadCount === 1
                ? '1 task was updated by someone else since your last visit'
                : `${unreadCount} tasks were updated by someone else since your last visit`}
            </strong>
            <p>Open a task to mark it as seen, or use Mark all as seen.</p>
          </div>
          <button type="button" className="task-outline-btn" onClick={markAllSeen}>
            Mark all as seen
          </button>
        </div>
      )}

      {view === 'list' && (
        <ProjectTaskList
          project={project}
          tasks={tasks}
          canManage={canManage}
          onOpenTask={openExistingTask}
          onAddStage={() => setStageDialog(true)}
          onNewTask={() => openNewTask()}
          onReorderStage={reorderStage}
          onDeleteStage={deleteStage}
          onSetFinished={setFinishedStage}
        />
      )}

      {view === 'kanban' && (
        <>
          <div className="list-view-head">
            <h2>Board</h2>
            <div className="list-view-actions">
              {canManage && (
                <Button label="+ Add Stage" className="p-button-outlined" onClick={() => setStageDialog(true)} />
              )}
              <Button label="+ New Task" onClick={() => openNewTask()} />
            </div>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
            <div className="projects-kanban">
              {columns.map((column, index) => (
                <Column
                  key={column.id}
                  id={column.id}
                  title={column.title}
                  color={column.color}
                  tasks={tasksByColumn[column.id] || []}
                  index={index}
                  total={columns.length}
                  finished={project.finishedColumnId === column.id}
                  canManage={canManage}
                  showSetFinished={canManage && !project.finishedColumnId}
                  onOpen={openExistingTask}
                  canOpenTask={canOpenTask}
                  onAdd={() => openNewTask(column.id)}
                  onReorder={(direction) => reorderStage(column.id, direction)}
                  onDelete={() => deleteStage(column.id)}
                  onSetFinished={() => setFinishedStage(column.id)}
                />
              ))}
            </div>
          </DndContext>
        </>
      )}

      {view === 'details' && isCreator && (
        <ProjectDetails
          project={project}
          users={users}
          onSaved={setProject}
          onDeleted={() => navigate('/projects')}
          onError={setError}
        />
      )}

      {stageDialog && (
        <div className="app-modal-overlay" onClick={() => { setStageDialog(false); setColumnSetFinished(false); }}>
          <div className="app-modal create-project-card" onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header">
              <div className="app-modal-header-text">
                <h2>Add stage</h2>
                <p>Create a new column on this board</p>
              </div>
              <button type="button" className="app-modal-close" onClick={() => { setStageDialog(false); setColumnSetFinished(false); }} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label htmlFor="stage-title">Title</label>
                <InputText
                  id="stage-title"
                  value={columnTitle}
                  onChange={(e) => setColumnTitle(e.target.value)}
                  placeholder="e.g. Review"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addStage();
                  }}
                />
              </div>
              {!project.finishedColumnId && (
                <label className="stage-finished-toggle">
                  <input
                    type="checkbox"
                    checked={columnSetFinished}
                    onChange={(e) => setColumnSetFinished(e.target.checked)}
                  />
                  <span>
                    <strong>Stage for finished tasks</strong>
                    <small>Only one stage can be marked. Tasks in it will not be suggested as dependencies.</small>
                  </span>
                </label>
              )}
              <div className="modal-actions">
                <Button label="Cancel" className="p-button-secondary" onClick={() => { setStageDialog(false); setColumnSetFinished(false); }} />
                <Button label="Add stage" onClick={addStage} />
              </div>
            </div>
          </div>
        </div>
      )}

      {modalTask !== undefined && (
        <ProjectTaskModal
          project={project}
          task={modalTask}
          users={users}
          tasks={tasks}
          initialStatus={newTaskStatus}
          focusCommentId={focusCommentId}
          onClose={closeModal}
          onSaved={(updated) => {
            setTasks((prev) => {
              const exists = prev.some((item) => item._id === updated._id);
              return exists ? prev.map((item) => (item._id === updated._id ? updated : item)) : [...prev, updated];
            });
            setModalTask((prev) => (prev ? updated : prev));
          }}
          onDeleted={(taskId) => {
            setTasks((prev) => prev.filter((item) => item._id !== taskId));
            closeModal();
          }}
        />
      )}
    </div>
  );
};

export default ProjectBoard;
